import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import { useAuth } from "@/hooks/useAuth";
import ImageUploader from "@/pages/seller/dashboard/components/ImageUploader";
import SmartImage from "@/pages/seller/dashboard/components/SmartImage";
import { uploadFile, uploadProductBlob } from "@/pages/seller/dashboard/lib";
import { generateTicket, burnTicketIntoImage } from "@/lib/ticket";
import { normalizeMediaList } from "@/lib/media";
import { loadApplication } from "@/lib/seller-application";
import {
  fetchCategories,
  topLevelCategories,
  subcategoriesOf,
} from "@/lib/categories";
import type { AnnouncementCategory } from "@/lib/categories";
import { formatPrice, missingAnnouncementFields } from "@/pages/seller/dashboard/types";
import type {
  SellerProduct,
  Currency,
  ProductCondition,
} from "@/pages/seller/dashboard/types";

const STEPS = [
  { label: "Información", icon: "ri-file-text-line" },
  { label: "Precio", icon: "ri-money-dollar-circle-line" },
  { label: "Fotos", icon: "ri-image-line" },
  { label: "Ubicación", icon: "ri-map-pin-line" },
  { label: "Disponibilidad", icon: "ri-stack-line" },
  { label: "Vista previa", icon: "ri-eye-line" },
];

const CURRENCIES: { value: Currency; label: string }[] = [
  { value: "DOP", label: "RD$ · Peso dominicano" },
  { value: "USD", label: "US$ · Dólar" },
];

const CONDITION_OPTIONS: { value: ProductCondition; label: string }[] = [
  { value: "nuevo", label: "Nuevo" },
  { value: "usado", label: "Usado" },
];

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

const labelClass = "mb-1.5 block text-sm font-semibold text-foreground-800";

const DRAFT_STORAGE_KEY = "vendeYa_anuncio_draft_id";

interface AnnouncementFormProps {
  editing: SellerProduct | null;
  userId: string;
  onSuccess: () => void;
  onCancelEdit: () => void;
}

export default function AnnouncementForm({
  editing,
  userId,
  onSuccess,
  onCancelEdit,
}: AnnouncementFormProps) {
  const { showToast } = useToast();
  const { role, profile } = useAuth();
  const isAdmin = role === "admin" || role === "super_admin";

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [draftId, setDraftId] = useState<number | null>(editing?.id ?? null);

  // Categorías administrables desde la base de datos.
  const [categories, setCategories] = useState<AnnouncementCategory[]>([]);

  // Paso 1 — Información básica
  const [title, setTitle] = useState(editing?.title ?? "");
  const [categoryId, setCategoryId] = useState<number | null>(
    editing?.category_id ?? null
  );
  const [subcategoryId, setSubcategoryId] = useState<number | null>(
    editing?.category_id ?? null
  );
  const [description, setDescription] = useState(editing?.description ?? "");
  const [brand, setBrand] = useState(editing?.brand ?? "");
  const [model, setModel] = useState(editing?.model ?? "");
  const [condition, setCondition] = useState<ProductCondition>(
    editing?.condition === "nuevo" ? "nuevo" : "usado"
  );

  // Paso 2 — Precio
  const [price, setPrice] = useState(editing ? String(editing.price) : "");
  const [negotiable, setNegotiable] = useState(editing?.negotiable ?? false);
  const [currency, setCurrency] = useState<Currency>(
    (editing?.currency as Currency) ?? "DOP"
  );
  const [minPrice, setMinPrice] = useState(
    editing?.min_price != null ? String(editing.min_price) : ""
  );

  // Paso 3 — Fotografías
  const [files, setFiles] = useState<File[]>([]);
  const [existingUrls, setExistingUrls] = useState<string[]>(
    normalizeMediaList(editing?.images)
  );
  const [ticket, setTicket] = useState<string | null>(
    editing?.ticket_number ?? null
  );
  const [ticketLoading, setTicketLoading] = useState(false);

  // Paso 4 — Ubicación
  const [province, setProvince] = useState(editing?.province ?? "");
  const [municipality, setMunicipality] = useState(editing?.municipality ?? "");
  const [sector, setSector] = useState(editing?.sector ?? "");

  // Paso 5 — Disponibilidad
  const [quantity, setQuantity] = useState(
    editing?.quantity ? String(editing.quantity) : "1"
  );

  const [stepError, setStepError] = useState<string | null>(null);
  const [processingLabel, setProcessingLabel] = useState<string | null>(null);

  const imageCount = files.length + existingUrls.length;
  const coverObjectUrl = useMemo(
    () => (files.length > 0 ? URL.createObjectURL(files[0]) : null),
    [files]
  );

  const topCategories = useMemo(() => topLevelCategories(categories), [categories]);
  const subcategories = useMemo(
    () => subcategoriesOf(categories, categoryId),
    [categories, categoryId]
  );

  // Carga de categorías.
  useEffect(() => {
    let active = true;
    fetchCategories().then((data) => {
      if (active) setCategories(data);
    });
    return () => {
      active = false;
    };
  }, []);

  // Pre-rellena la ubicación con la del vendedor al crear un anuncio nuevo.
  useEffect(() => {
    if (editing) return; // solo al crear, no al editar
    let active = true;
    (async () => {
      // Fuente principal: la ubicación confirmada en la solicitud de vendedor.
      try {
        const app = await loadApplication(userId);
        if (!active) return;
        if (app?.province || app?.city) {
          setProvince((prev) => prev || app.province || "");
          setMunicipality((prev) => prev || app.city || "");
          return;
        }
      } catch {
        // cae al fallback del perfil
      }
      // Fallback: ubicación guardada en el perfil.
      if (profile?.province || profile?.city) {
        setProvince((prev) => prev || profile.province || "");
        setMunicipality((prev) => prev || profile.city || "");
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, userId]);

  // Genera el ticket al entrar (reutiliza pendientes, no crea huérfanos).
  useEffect(() => {
    if (isAdmin || ticket || ticketLoading) return;
    setTicketLoading(true);
    generateTicket()
      .then((t) => setTicket(t))
      .catch(() => {
        /* se reintenta al publicar */
      })
      .finally(() => setTicketLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDraftable =
    !editing || editing.status === "draft" || editing.status === "pending_completion";

  const hasContent = title.trim().length > 0;

  // Autosave silencioso (borrador) con debounce de 1.5s.
  const autosave = useCallback(async () => {
    if (!hasContent || !isDraftable || submitting) return;
    setSaveState("saving");
    try {
      const payload = {
        title: title.trim() || "Anuncio sin título",
        description: description.trim() || null,
        price: Number(price) || 0,
        currency,
        category: categoryId ? categoryName(categoryId) : null,
        category_id: subcategoryId || categoryId,
        brand: brand.trim() || null,
        model: model.trim() || null,
        condition,
        negotiable,
        min_price: negotiable ? Number(minPrice) || null : null,
        province: province.trim() || null,
        municipality: municipality.trim() || null,
        sector: sector.trim() || null,
        quantity: Number(quantity) || 1,
      };
      if (draftId) {
        await supabase.from("products").update(payload).eq("id", draftId);
      } else {
        const { data, error } = await supabase
          .from("products")
          .insert({ ...payload, seller_id: userId, status: "draft" })
          .select("id");
        if (error) throw error;
        const newId = (data as { id: number }[] | null)?.[0]?.id;
        if (newId) {
          setDraftId(newId);
          localStorage.setItem(DRAFT_STORAGE_KEY, String(newId));
        }
      }
      setSaveState("saved");
    } catch {
      setSaveState("idle");
    }
  }, [
    hasContent,
    isDraftable,
    submitting,
    title,
    description,
    price,
    currency,
    categoryId,
    subcategoryId,
    brand,
    model,
    condition,
    negotiable,
    minPrice,
    province,
    municipality,
    sector,
    quantity,
    draftId,
    userId,
  ]);

  function categoryName(id: number): string | null {
    return categories.find((c) => c.id === id)?.name ?? null;
  }

  useEffect(() => {
    if (!hasContent || !isDraftable || submitting) return;
    const t = setTimeout(() => {
      autosave();
    }, 1500);
    return () => clearTimeout(t);
  }, [
    hasContent,
    isDraftable,
    submitting,
    title,
    description,
    price,
    currency,
    categoryId,
    subcategoryId,
    brand,
    model,
    condition,
    negotiable,
    minPrice,
    province,
    municipality,
    sector,
    quantity,
    autosave,
  ]);

  // Advertencia al salir con cambios sin guardar.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasContent && saveState !== "saved") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasContent, saveState]);

  function buildPayload() {
    return {
      title: title.trim(),
      description: description.trim() || null,
      price: Number(price),
      currency,
      category: categoryId ? categoryName(categoryId) : null,
      category_id: subcategoryId || categoryId,
      condition,
      brand: brand.trim() || null,
      model: model.trim() || null,
      negotiable,
      min_price: negotiable ? Number(minPrice) || null : null,
      province: province.trim() || null,
      municipality: municipality.trim() || null,
      sector: sector.trim() || null,
      quantity: Number(quantity) || 1,
    };
  }

  function missingItems(): string[] {
    const preview: Partial<SellerProduct> = {
      title,
      description,
      price: Number(price),
      category: categoryId ? "x" : null,
      category_id: categoryId,
      images: existingUrls.length > 0 ? existingUrls : files.length > 0 ? ["x"] : [],
      province,
    };
    return missingAnnouncementFields(preview, isAdmin);
  }

  function validateStep(s: number): string | null {
    if (s === 0) {
      if (title.trim().length < 3)
        return "Escribe un título de al menos 3 caracteres.";
      if (!categoryId) return "Selecciona una categoría.";
    }
    if (s === 1) {
      const p = Number(price);
      if (!price.trim() || Number.isNaN(p) || p <= 0)
        return "Ingresa un precio válido mayor a 0.";
    }
    if (s === 2 && imageCount === 0)
      return "Agrega al menos una foto del producto.";
    if (s === 4) {
      const q = Number(quantity);
      if (!quantity.trim() || Number.isNaN(q) || q < 1)
        return "La cantidad debe ser al menos 1.";
    }
    return null;
  }

  function next() {
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function back() {
    setStepError(null);
    setStep((s) => Math.max(s - 1, 0));
  }

  async function saveDraft(showFeedback = true) {
    if (!hasContent) {
      showToast("Escribe al menos un título para guardar el borrador.", "warning");
      return;
    }
    await autosave();
    if (showFeedback) {
      showToast(
        "Tu anuncio fue guardado como borrador. Puedes continuar cuando quieras.",
        "success"
      );
    }
  }

  async function handleSubmit() {
    const missing = missingItems();
    if (missing.length > 0) {
      setStep(STEPS.length - 1);
      setStepError(
        `Falta completar: ${missing.join(", ")}. Revisa el anuncio antes de enviarlo.`
      );
      return;
    }

    setSubmitting(true);
    if (files.length > 0) setProcessingLabel("Procesando imágenes…");
    try {
      let currentTicket = isAdmin ? null : ticket;
      if (!isAdmin && files.length > 1 && !currentTicket) {
        currentTicket = await generateTicket();
        setTicket(currentTicket);
      }

      const uploadedPaths: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (i === 0 || isAdmin) {
          uploadedPaths.push(await uploadFile(file, userId));
        } else {
          if (!currentTicket)
            throw new Error("Generá el ticket antes de publicar.");
          const blob = await burnTicketIntoImage(file, currentTicket);
          uploadedPaths.push(await uploadProductBlob(blob, userId, file.name));
        }
      }
      const images = [...existingUrls, ...uploadedPaths];

      const payload = {
        ...buildPayload(),
        images,
        ticket_number: currentTicket,
      };

      const finalStatus = isAdmin ? "active" : "pending_moderation";

      if (editing) {
        const isDraft =
          editing.status === "draft" || editing.status === "pending_completion";
        await supabase
          .from("products")
          .update({
            ...payload,
            status: isDraft ? finalStatus : editing.status,
            published_at:
              finalStatus === "active" || editing.status === "active"
                ? new Date().toISOString()
                : editing.published_at,
          })
          .eq("id", editing.id);
        showToast(
          isAdmin
            ? "¡Anuncio actualizado! Ya está visible."
            : "Tu anuncio fue enviado a revisión.",
          "success"
        );
      } else {
        await supabase.from("products").insert({
          ...payload,
          seller_id: userId,
          status: finalStatus,
          published_at: finalStatus === "active" ? new Date().toISOString() : null,
        });
        showToast(
          isAdmin
            ? "¡Anuncio publicado! Ya está disponible en VendeYa."
            : "Tu anuncio fue enviado correctamente y está pendiente de revisión.",
          "success"
        );
      }

      localStorage.removeItem(DRAFT_STORAGE_KEY);
      onSuccess();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "No se pudo enviar el anuncio.",
        "error"
      );
    } finally {
      setSubmitting(false);
      setProcessingLabel(null);
    }
  }

  const missing = missingItems();

  return (
    <div className="p-5 md:p-8">
      {editing && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-100/60 px-4 py-3">
          <i className="ri-edit-line text-primary-600"></i>
          <p className="text-sm font-medium text-primary-800">
            {editing.status === "draft" ||
            editing.status === "pending_completion"
              ? `Continuando «${editing.title || "borrador"}».`
              : `Editando «${editing.title}».`}
          </p>
          <button
            type="button"
            onClick={onCancelEdit}
            className="ml-auto cursor-pointer whitespace-nowrap text-sm font-semibold text-primary-700 hover:text-primary-900"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Barra de pasos */}
      <ol className="mb-6 flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => (
          <li key={s.label} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => i < step && back()}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold ${
                i === step
                  ? "bg-primary-500 text-white"
                  : i < step
                    ? "text-primary-600"
                    : "text-foreground-400"
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                  i < step
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-200 text-foreground-500"
                }`}
              >
                {i < step ? (
                  <i className="ri-check-line"></i>
                ) : (
                  <span>{i + 1}</span>
                )}
              </span>
              {s.label}
            </button>
            {i < STEPS.length - 1 && (
              <span className="h-px w-4 bg-background-200"></span>
            )}
          </li>
        ))}
      </ol>

      {/* Indicador de autosave */}
      <div className="mb-5 flex min-h-5 items-center gap-1.5 text-xs text-foreground-500">
        {saveState === "saving" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            Guardando…
          </>
        ) : saveState === "saved" ? (
          <>
            <i className="ri-check-line text-secondary-600"></i>
            Guardado automáticamente
          </>
        ) : null}
      </div>

      {/* Paso 0 — Información básica */}
      {step === 0 && (
        <div className="space-y-5">
          <div>
            <label className={labelClass} htmlFor="a-title">
              Título del anuncio
            </label>
            <input
              id="a-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej. iPhone 13 Pro 128GB en excelente estado"
              className={inputClass}
              maxLength={120}
            />
            <p className="mt-1 text-xs text-foreground-400">
              {title.length}/120 caracteres
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="a-category">
                Categoría
              </label>
              <select
                id="a-category"
                value={categoryId ?? ""}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  setCategoryId(id);
                  setSubcategoryId(id);
                }}
                className={inputClass}
              >
                <option value="">Selecciona una categoría</option>
                {topCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass} htmlFor="a-subcategory">
                Subcategoría
              </label>
              <select
                id="a-subcategory"
                value={subcategoryId ?? ""}
                onChange={(e) =>
                  setSubcategoryId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={subcategories.length === 0}
                className={`${inputClass} disabled:opacity-50`}
              >
                <option value="">Sin subcategoría</option>
                {subcategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="a-description">
              Descripción detallada
            </label>
            <textarea
              id="a-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={1500}
              placeholder="Características, estado, motivo de venta…"
              className={`${inputClass} resize-none`}
            ></textarea>
            <p className="mt-1 text-xs text-foreground-400">
              {description.length}/1500 caracteres
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div>
              <label className={labelClass} htmlFor="a-brand">
                Marca
              </label>
              <input
                id="a-brand"
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Ej. Apple"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="a-model">
                Modelo
              </label>
              <input
                id="a-model"
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="Ej. iPhone 13 Pro"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Condición</label>
              <div className="grid grid-cols-2 gap-2">
                {CONDITION_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCondition(c.value)}
                    className={`cursor-pointer whitespace-nowrap rounded-md border px-2 py-2 text-sm font-semibold transition-colors ${
                      condition === c.value
                        ? "border-primary-400 bg-primary-100 text-primary-800"
                        : "border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Paso 1 — Precio */}
      {step === 1 && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="a-price">
                Precio
              </label>
              <input
                id="a-price"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="a-currency">
                Moneda
              </label>
              <select
                id="a-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
                className={inputClass}
              >
                {CURRENCIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={negotiable}
                onChange={(e) => setNegotiable(e.target.checked)}
                className="h-4 w-4 rounded border-background-300 text-primary-500"
              />
              <span className="text-sm font-semibold text-foreground-800">
                Permitir negociación
              </span>
            </label>
            {negotiable && (
              <div className="mt-3">
                <label className={labelClass} htmlFor="a-minprice">
                  Precio mínimo aceptado
                </label>
                <input
                  id="a-minprice"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Paso 2 — Fotografías */}
      {step === 2 && (
        <div className="space-y-6">
          {!isAdmin && (
            <div className="flex items-center gap-3 rounded-md border border-primary-200 bg-primary-100/50 p-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                {ticketLoading ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : ticket ? (
                  <i className="ri-ticket-2-fill"></i>
                ) : (
                  <i className="ri-ticket-2-line"></i>
                )}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-primary-800">
                  {ticketLoading
                    ? "Generando ticket…"
                    : ticket
                      ? `Ticket: ${ticket}`
                      : "Ticket pendiente"}
                </p>
                <p className="text-xs text-foreground-600">
                  La portada puede ser promocional. Las fotos de galería (desde
                  la 2da) se marcan automáticamente con este ticket.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>Fotografías del producto</label>
            <ImageUploader
              files={files}
              existingUrls={existingUrls}
              onFilesChange={setFiles}
              onRemoveExisting={(i) =>
                setExistingUrls((prev) => prev.filter((_, idx) => idx !== i))
              }
            />
          </div>
        </div>
      )}

      {/* Paso 3 — Ubicación */}
      {step === 3 && (
        <div className="space-y-5">
          <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-100/50 p-3 text-sm text-foreground-700">
            <i className="ri-map-pin-line mt-0.5 text-secondary-600"></i>
            <p>
              Prellenamos tu ubicación desde tu perfil de vendedor. Podés
              ajustarla si el producto está en otra zona.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="a-province">
                Provincia
              </label>
              <input
                id="a-province"
                type="text"
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                placeholder="Ej. Santo Domingo"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="a-municipality">
                Municipio
              </label>
              <input
                id="a-municipality"
                type="text"
                value={municipality}
                onChange={(e) => setMunicipality(e.target.value)}
                placeholder="Ej. Distrito Nacional"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass} htmlFor="a-sector">
              Sector / Zona (opcional)
            </label>
            <input
              id="a-sector"
              type="text"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              placeholder="Ej. Piantini"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-foreground-400">
              Nunca mostramos tu dirección exacta ni datos privados.
            </p>
          </div>
        </div>
      )}

      {/* Paso 4 — Disponibilidad */}
      {step === 4 && (
        <div className="space-y-5">
          <div className="max-w-xs">
            <label className={labelClass} htmlFor="a-quantity">
              Cantidad disponible
            </label>
            <input
              id="a-quantity"
              type="number"
              inputMode="numeric"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className={inputClass}
            />
          </div>
          <p className="text-sm text-foreground-500">
            Indica cuántas unidades de este artículo tienes disponibles para
            vender.
          </p>
        </div>
      )}

      {/* Paso 5 — Vista previa */}
      {step === 5 && (
        <div className="space-y-5">
          <h3 className="font-heading text-base font-bold text-foreground-950">
            Revisar anuncio
          </h3>

          {/* Checklist */}
          <ul className="grid grid-cols-1 gap-2 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-2">
            {[
              { ok: title.trim().length >= 3, label: "Información del producto" },
              { ok: Number(price) > 0, label: "Precio" },
              { ok: !!categoryId, label: "Categoría" },
              { ok: imageCount >= 1, label: "Fotografías" },
              {
                ok: isAdmin ? true : imageCount >= 2,
                label: "Fotografía de verificación",
              },
              { ok: province.trim().length > 0, label: "Ubicación" },
              { ok: description.trim().length >= 3, label: "Descripción" },
            ].map((item) => (
              <li
                key={item.label}
                className="flex items-center gap-2 text-sm"
              >
                <i
                  className={`${
                    item.ok
                      ? "ri-checkbox-circle-fill text-secondary-600"
                      : "ri-close-circle-fill text-accent-500"
                  }`}
                ></i>
                <span
                  className={item.ok ? "text-foreground-800" : "text-foreground-500"}
                >
                  {item.label}
                </span>
              </li>
            ))}
          </ul>

          {/* Tarjeta previa */}
          <div className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100">
              {coverObjectUrl ? (
                <img
                  src={coverObjectUrl}
                  alt="Portada"
                  className="h-full w-full object-cover object-top"
                />
              ) : existingUrls.length > 0 ? (
                <SmartImage
                  src={existingUrls[0]}
                  alt="Portada"
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-foreground-300">
                  <i className="ri-image-line text-2xl"></i>
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                {title || "Sin título"}
              </h3>
              <p className="mt-1 font-heading text-lg font-bold text-primary-600">
                {formatPrice(Number(price) || 0, currency)}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {categoryId && (
                  <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-700">
                    {categoryName(categoryId)}
                  </span>
                )}
                <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-xs font-medium text-foreground-600">
                  {condition === "nuevo" ? "Nuevo" : "Usado"}
                </span>
              </div>
            </div>
          </div>

          <p className="text-sm text-foreground-500">
            {isAdmin
              ? "Tu anuncio se publicará directamente y estará visible para todos."
              : "Al enviar, tu anuncio pasará a revisión por un moderador antes de quedar disponible."}
          </p>
        </div>
      )}

      {/* Error */}
      {stepError && (
        <p className="mt-4 flex items-start gap-1.5 text-sm font-medium text-accent-700">
          <i className="ri-error-warning-line mt-0.5"></i>
          {stepError}
        </p>
      )}

      {/* Procesamiento de imágenes */}
      {processingLabel && (
        <p className="mt-3 flex items-center gap-2 text-sm font-medium text-secondary-700">
          <i className="ri-loader-4-line animate-spin"></i>
          {processingLabel}
        </p>
      )}

      {/* Botones */}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-line"></i>
            Atrás
          </button>
          {step > 0 && (
            <button
              type="button"
              onClick={() => saveDraft()}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-save-line"></i>
              Guardar borrador
            </button>
          )}
        </div>

        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={next}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Continuar
            <i className="ri-arrow-right-line"></i>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => saveDraft()}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-save-line"></i>
              Guardar borrador
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enviando…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  {isAdmin ? "Publicar anuncio" : "Enviar para revisión"}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}